import React, { useEffect, useRef, useState } from 'react';

// Constellation Canvas Component
const ConstellationCanvas = ({ theme }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const nodesRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let width = window.innerWidth;
    let height = window.innerHeight;

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
      initNodes();
    };

    const initNodes = () => {
      const nodeCount = Math.floor((width * height) / 15000);
      nodesRef.current = [];
      for (let i = 0; i < nodeCount; i++) {
        nodesRef.current.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.3,
          radius: Math.random() * 2 + 1,
          pulse: Math.random() * Math.PI * 2,
        });
      }
    };

    const draw = () => {
      const isDark = theme === 'dark';
      ctx.fillStyle = isDark
        ? 'rgba(10, 22, 40, 0.1)'
        : 'rgba(239, 246, 255, 0.1)';
      ctx.fillRect(0, 0, width, height);

      const nodeColor = isDark ? 'rgba(147, 197, 253, ' : 'rgba(59, 130, 246, ';
      const lineColor = isDark ? 'rgba(147, 197, 253, ' : 'rgba(59, 130, 246, ';

      // Update and draw nodes
      nodesRef.current.forEach((node, i) => {
        node.x += node.vx;
        node.y += node.vy;
        node.pulse += 0.02;

        // Bounce off edges
        if (node.x < 0 || node.x > width) node.vx *= -1;
        if (node.y < 0 || node.y > height) node.vy *= -1;

        // Pulse effect
        const pulseScale = 1 + Math.sin(node.pulse) * 0.3;
        const radius = node.radius * pulseScale;

        // Draw node
        ctx.beginPath();
        ctx.arc(node.x, node.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = nodeColor + (0.4 + Math.sin(node.pulse) * 0.2) + ')';
        ctx.fill();

        // Draw connections
        nodesRef.current.slice(i + 1).forEach(other => {
          const dx = other.x - node.x;
          const dy = other.y - node.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 150) {
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(other.x, other.y);
            ctx.strokeStyle = lineColor + (0.15 * (1 - dist / 150)) + ')';
            ctx.lineWidth = 1;
            ctx.stroke();
          }
        });
      });

      animationRef.current = requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener('resize', resize);
    draw();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationRef.current);
    };
  }, [theme]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
};

// Animated Counter Component
const AnimatedCounter = ({ end, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          let start = 0;
          const increment = end / (duration / 16);
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    if (countRef.current) observer.observe(countRef.current);
    return () => observer.disconnect();
  }, [end, duration]);

  return <span ref={countRef}>{count.toLocaleString()}{suffix}</span>;
};


// Feature Card Component
const FeatureCard = ({ icon, title, description, delay, theme }) => (
  <div
    className={`
      group relative p-8 rounded-3xl
      bg-white/70 dark:bg-navy-800/50
      backdrop-blur-xl border border-silver-200/50 dark:border-silver-700/20
      hover:border-alpha-400/50 dark:hover:border-alpha-500/30
      transform hover:-translate-y-2 hover:scale-[1.02]
      transition-all duration-500 ease-out
      animate-fade-in
    `}
    style={{ animationDelay: `${delay}ms` }}
  >
    {/* Gradient overlay on hover */}
    <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-alpha-500/0 to-alpha-600/0 group-hover:from-alpha-500/5 group-hover:to-alpha-600/10 transition-all duration-500" />

    {/* Icon container */}
    <div className="relative mb-6 w-16 h-16 rounded-2xl bg-alpha-gradient flex items-center justify-center shadow-alpha group-hover:shadow-alpha-lg transition-shadow duration-500">
      <span className="text-3xl">{icon}</span>
    </div>

    <h3 className="relative text-xl font-bold text-silver-800 dark:text-white mb-3 group-hover:text-alpha-600 dark:group-hover:text-alpha-400 transition-colors duration-300">
      {title}
    </h3>
    <p className="relative text-silver-600 dark:text-silver-400 leading-relaxed">
      {description}
    </p>

    {/* Corner accent */}
    <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-silver-200/30 dark:border-silver-600/20 rounded-tr-2xl group-hover:border-alpha-400/50 transition-colors duration-500" />
  </div>
);

// Step Component
const Step = ({ number, title, description, isLast }) => (
  <div className="relative flex flex-col items-center text-center group">
    {/* Connector line */}
    {!isLast && (
      <div className="hidden md:block absolute top-10 left-[60%] w-full h-0.5 bg-gradient-to-r from-alpha-400 to-alpha-600/30" />
    )}

    {/* Number circle */}
    <div className="relative z-10 w-20 h-20 rounded-full bg-alpha-gradient flex items-center justify-center text-white text-2xl font-bold shadow-alpha group-hover:shadow-alpha-lg group-hover:scale-110 transition-all duration-500">
      {number}
    </div>

    <h4 className="mt-6 text-xl font-bold text-silver-800 dark:text-white">
      {title}
    </h4>
    <p className="mt-2 text-silver-600 dark:text-silver-400 max-w-xs">
      {description}
    </p>
  </div>
);

// App Store Badge Component
const AppStoreBadge = ({ height = 40, className = '' }) => (
  <a
    href="https://apps.apple.com/gb/app/heyalphas/id6748565686"
    target="_blank"
    rel="noopener noreferrer"
    aria-label="Download on the App Store"
    className={`inline-block hover:scale-105 transition-transform duration-200 ${className}`}
  >
    <svg height={height} viewBox="0 0 120 40" xmlns="http://www.w3.org/2000/svg">
      <rect width="120" height="40" rx="6" fill="#000"/>
      <path d="M24.769 20.3a4.949 4.949 0 012.356-4.151 5.066 5.066 0 00-3.99-2.158c-1.68-.176-3.308 1.005-4.164 1.005-.872 0-2.19-.988-3.608-.958a5.315 5.315 0 00-4.473 2.728c-1.934 3.348-.491 8.269 1.361 10.976.927 1.325 2.01 2.805 3.428 2.753 1.387-.058 1.905-.885 3.58-.885 1.658 0 2.144.885 3.59.852 1.489-.025 2.426-1.332 3.32-2.67a10.962 10.962 0 001.52-3.092 4.782 4.782 0 01-2.92-4.4zM22.037 12.21a4.872 4.872 0 001.115-3.49 4.957 4.957 0 00-3.208 1.66 4.636 4.636 0 00-1.144 3.36 4.1 4.1 0 003.237-1.53z" fill="#fff"/>
      <path d="M42.302 27.14h-4.733l-1.137 3.356h-2.005l4.484-12.418h2.083l4.483 12.418h-2.039l-1.136-3.356zm-4.243-1.55h3.752l-1.85-5.446h-.051l-1.85 5.447zM55.16 25.97c0 2.813-1.506 4.621-3.779 4.621a3.07 3.07 0 01-2.848-1.584h-.043v4.484h-1.859V21.442h1.799v1.506h.034a3.212 3.212 0 012.883-1.6c2.298 0 3.813 1.816 3.813 4.622zm-1.91 0c0-1.833-.948-3.038-2.393-3.038-1.42 0-2.375 1.23-2.375 3.038 0 1.824.955 3.046 2.375 3.046 1.445 0 2.393-1.197 2.393-3.046zM65.125 25.97c0 2.813-1.506 4.621-3.779 4.621a3.07 3.07 0 01-2.848-1.584h-.043v4.484h-1.859V21.442h1.8v1.506h.033a3.212 3.212 0 012.883-1.6c2.298 0 3.813 1.816 3.813 4.622zm-1.91 0c0-1.833-.948-3.038-2.393-3.038-1.42 0-2.375 1.23-2.375 3.038 0 1.824.955 3.046 2.375 3.046 1.445 0 2.393-1.197 2.393-3.046zM71.71 27.036c.138 1.232 1.335 2.04 2.97 2.04 1.566 0 2.693-.808 2.693-1.919 0-.964-.68-1.54-2.29-1.936l-1.609-.388c-2.28-.55-3.339-1.617-3.339-3.348 0-2.142 1.867-3.614 4.519-3.614 2.624 0 4.423 1.472 4.483 3.614h-1.876c-.112-1.239-1.137-1.987-2.634-1.987-1.496 0-2.521.757-2.521 1.858 0 .878.654 1.395 2.255 1.79l1.368.336c2.548.603 3.606 1.626 3.606 3.443 0 2.323-1.85 3.778-4.793 3.778-2.754 0-4.614-1.42-4.734-3.667h1.902zM83.346 19.3v2.142h1.722v1.472h-1.722v4.991c0 .776.345 1.137 1.102 1.137.204-.004.408-.018.611-.043v1.463a5.104 5.104 0 01-1.032.086c-1.833 0-2.548-.689-2.548-2.444v-5.19h-1.316v-1.472h1.316V19.3h1.867zM86.065 25.97c0-2.849 1.678-4.639 4.294-4.639 2.625 0 4.295 1.79 4.295 4.639 0 2.856-1.661 4.638-4.295 4.638-2.633 0-4.294-1.782-4.294-4.638zm6.695 0c0-1.954-.895-3.108-2.401-3.108s-2.401 1.162-2.401 3.108c0 1.962.895 3.106 2.401 3.106s2.401-1.144 2.401-3.106zM96.186 21.442h1.773v1.541h.043a2.16 2.16 0 012.178-1.635 2.866 2.866 0 01.637.069v1.738a2.598 2.598 0 00-.835-.112 1.873 1.873 0 00-1.937 2.083v5.37h-1.859v-9.054zM109.384 27.837c-.25 1.643-1.85 2.771-3.898 2.771-2.634 0-4.269-1.764-4.269-4.595 0-2.84 1.644-4.682 4.19-4.682 2.506 0 4.08 1.72 4.08 4.466v.637h-6.395v.112a2.358 2.358 0 002.436 2.564 2.048 2.048 0 002.09-1.273h1.766zm-6.282-2.702h4.526a2.177 2.177 0 00-2.22-2.298 2.292 2.292 0 00-2.306 2.298z" fill="#fff"/>
      <path d="M37.826 8.731a2.64 2.64 0 012.808 2.965c0 1.906-1.03 3.002-2.808 3.002h-2.155V8.731h2.155zm-1.228 5.123h1.125a1.876 1.876 0 001.967-2.146 1.881 1.881 0 00-1.967-2.134h-1.125v4.28zM41.68 12.444a2.133 2.133 0 114.248 0 2.134 2.134 0 11-4.247 0zm3.334 0c0-.976-.439-1.547-1.208-1.547-.773 0-1.207.571-1.207 1.547 0 .984.434 1.55 1.207 1.55.77 0 1.208-.57 1.208-1.55zM51.573 14.698h-.922l-.93-3.317h-.07l-.927 3.317h-.913l-1.242-4.503h.902l.806 3.436h.067l.926-3.436h.852l.926 3.436h.07l.803-3.436h.889l-1.237 4.503zM53.854 10.195h.855v.715h.066a1.348 1.348 0 011.344-.802 1.465 1.465 0 011.559 1.675v2.915h-.889v-2.692c0-.724-.314-1.084-.972-1.084a1.033 1.033 0 00-1.075 1.141v2.635h-.888v-4.503zM59.094 8.437h.888v6.26h-.888v-6.26zM61.218 12.444a2.133 2.133 0 114.247 0 2.134 2.134 0 11-4.247 0zm3.333 0c0-.976-.439-1.547-1.208-1.547-.773 0-1.207.571-1.207 1.547 0 .984.434 1.55 1.207 1.55.77 0 1.208-.57 1.208-1.55zM66.4 13.424c0-.81.604-1.278 1.676-1.344l1.22-.07v-.389c0-.476-.315-.744-.922-.744-.497 0-.84.182-.939.5h-.86c.09-.773.818-1.269 1.84-1.269 1.128 0 1.765.563 1.765 1.514v3.076h-.855v-.633h-.07a1.515 1.515 0 01-1.353.707 1.36 1.36 0 01-1.501-1.348zm2.895-.384v-.377l-1.1.07c-.62.042-.9.253-.9.65 0 .405.351.64.834.64a1.062 1.062 0 001.166-.983zM71.348 12.444c0-1.423.732-2.324 1.87-2.324a1.484 1.484 0 011.38.79h.067V8.437h.888v6.26h-.851v-.71h-.07a1.563 1.563 0 01-1.415.785c-1.145 0-1.869-.901-1.869-2.328zm.918 0c0 .955.45 1.53 1.203 1.53.75 0 1.212-.583 1.212-1.526 0-.938-.468-1.53-1.212-1.53-.748 0-1.203.58-1.203 1.526zM79.23 12.444a2.133 2.133 0 114.247 0 2.134 2.134 0 11-4.247 0zm3.333 0c0-.976-.438-1.547-1.208-1.547-.772 0-1.207.571-1.207 1.547 0 .984.435 1.55 1.207 1.55.77 0 1.208-.57 1.208-1.55zM84.67 10.195h.855v.715h.066a1.348 1.348 0 011.344-.802 1.465 1.465 0 011.559 1.675v2.915h-.889v-2.692c0-.724-.314-1.084-.972-1.084a1.033 1.033 0 00-1.075 1.141v2.635h-.888v-4.503zM93.515 9.074v1.141h.976v.749h-.976v2.315c0 .472.194.679.637.679.113 0 .226-.007.339-.02v.74a2.966 2.966 0 01-.484.046c-.988 0-1.381-.348-1.381-1.216v-2.544h-.715v-.749h.715V9.074h.889zM95.705 8.437h.88v2.481h.07a1.386 1.386 0 011.374-.806 1.483 1.483 0 011.55 1.679v2.907h-.889V12.01c0-.72-.335-1.084-.963-1.084a1.052 1.052 0 00-1.134 1.142v2.63h-.888V8.437zM104.761 13.482a1.823 1.823 0 01-1.951 1.303 2.047 2.047 0 01-2.08-2.325 2.077 2.077 0 012.076-2.352c1.253 0 2.009.856 2.009 2.27v.31h-3.18v.05a1.19 1.19 0 001.2 1.29 1.08 1.08 0 001.07-.546h.856zm-3.126-1.451h2.275a1.086 1.086 0 00-1.109-1.167 1.152 1.152 0 00-1.166 1.167z" fill="#fff"/>
    </svg>
  </a>
);

// Google Play Badge Component
const GooglePlayBadge = ({ height = 40, className = '' }) => (
  <a
    href="https://play.google.com/store/apps/details?id=com.heybonk.android"
    target="_blank"
    rel="noopener noreferrer"
    aria-label="Get it on Google Play"
    className={`inline-block hover:scale-105 transition-transform duration-200 ${className}`}
  >
    <svg height={height} viewBox="0 0 135 40" xmlns="http://www.w3.org/2000/svg">
      <rect width="135" height="40" rx="5" fill="#000"/>
      <path d="M68.135 21.75a4.251 4.251 0 100 8.5 4.251 4.251 0 000-8.5zm0 6.83a2.58 2.58 0 110-5.16 2.58 2.58 0 010 5.16zm-9.28-6.83a4.25 4.25 0 100 8.5 4.25 4.25 0 000-8.5zm0 6.83a2.58 2.58 0 110-5.16 2.58 2.58 0 010 5.16zm-11.04-5.52v1.8h4.32a3.77 3.77 0 01-.98 2.27 4.42 4.42 0 01-3.34 1.32 4.8 4.8 0 010-9.6 4.6 4.6 0 013.26 1.29l1.27-1.27a6.29 6.29 0 00-4.53-1.82 6.61 6.61 0 100 13.21 6.05 6.05 0 004.62-1.86 6 6 0 001.56-4.23 5.92 5.92 0 00-.1-1.13h-6.08zm45.2 1.4a3.94 3.94 0 00-3.63-2.71 4 4 0 00-3.98 4.25 4.15 4.15 0 004.16 4.25 4.18 4.18 0 003.5-1.86l-1.43-.95a2.4 2.4 0 01-2.07 1.16 2.14 2.14 0 01-2.04-1.28l5.63-2.33-.14-.53zm-5.75 1.4a2.32 2.32 0 012.21-2.47 1.64 1.64 0 011.58.9l-3.79 1.57zm-4.62 4.12h1.87V16.25h-1.87V30zm-3.06-8.03h-.07a2.96 2.96 0 00-2.24-.97 4.25 4.25 0 000 8.5 2.9 2.9 0 002.24-1h.06v.6c0 1.6-.86 2.46-2.23 2.46a2.31 2.31 0 01-2.1-1.49l-1.63.68a4.03 4.03 0 003.73 2.49c2.17 0 4-1.28 4-4.4v-7.58h-1.76v.71zm-2.1 5.87a2.59 2.59 0 010-5.15 2.4 2.4 0 012.27 2.6 2.38 2.38 0 01-2.27 2.55zm24.34-11.09h-4.47V30h1.87v-5.2h2.6a3.88 3.88 0 100-7.75zm.05 6.02h-2.65v-4.28h2.65a2.14 2.14 0 110 4.28zm11.52-1.79a3.5 3.5 0 00-3.32 1.91l1.66.69a1.77 1.77 0 011.7-.92 1.8 1.8 0 012 1.61v.13a4.13 4.13 0 00-1.95-.48c-1.79 0-3.6.98-3.6 2.81a2.89 2.89 0 003.1 2.75 2.63 2.63 0 002.4-1.2h.06v.96h1.8v-4.8c0-2.21-1.66-3.46-3.8-3.46h-.05zm-.22 6.84c-.62 0-1.47-.3-1.47-1.06 0-.97 1.06-1.34 1.98-1.34a3.34 3.34 0 011.7.42 2.26 2.26 0 01-2.21 1.98zm10.58-6.56l-2.14 5.42h-.06l-2.22-5.42h-2.01l3.33 7.58-1.9 4.21h1.95l5.13-11.79h-2.08zm-16.83 8h1.87V16.25h-1.87V30z" fill="#fff"/>
      <path d="M10.44 7.54a2 2 0 00-.46 1.4v22.12a2 2 0 00.46 1.4l.07.07 12.39-12.4v-.26L10.5 7.47l-.07.07z" fill="url(#a)"/>
      <path d="M27.03 24.27l-4.13-4.13v-.29l4.13-4.13.1.05 4.9 2.78c1.4.79 1.4 2.09 0 2.89l-4.9 2.78-.1.05z" fill="url(#b)"/>
      <path d="M27.12 24.22L22.9 20 10.44 32.46a1.63 1.63 0 002.08.06l14.6-8.3" fill="url(#c)"/>
      <path d="M27.12 15.78l-14.6-8.3a1.63 1.63 0 00-2.08.06L22.9 20l4.22-4.22z" fill="url(#d)"/>
      <defs>
        <linearGradient id="a" x1="21.8" y1="8.71" x2="5.02" y2="25.49" gradientUnits="userSpaceOnUse">
          <stop stopColor="#00A0FF"/>
          <stop offset=".01" stopColor="#00A1FF"/>
          <stop offset=".26" stopColor="#00BEFF"/>
          <stop offset=".51" stopColor="#00D2FF"/>
          <stop offset=".76" stopColor="#00DFFF"/>
          <stop offset="1" stopColor="#00E3FF"/>
        </linearGradient>
        <linearGradient id="b" x1="33.83" y1="20" x2="9.64" y2="20" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FFE000"/>
          <stop offset=".41" stopColor="#FFBD00"/>
          <stop offset=".78" stopColor="#FFA500"/>
          <stop offset="1" stopColor="#FF9C00"/>
        </linearGradient>
        <linearGradient id="c" x1="24.83" y1="22.3" x2="2.07" y2="45.05" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FF3A44"/>
          <stop offset="1" stopColor="#C31162"/>
        </linearGradient>
        <linearGradient id="d" x1="7.3" y1=".18" x2="17.46" y2="10.34" gradientUnits="userSpaceOnUse">
          <stop stopColor="#32A071"/>
          <stop offset=".07" stopColor="#2DA771"/>
          <stop offset=".48" stopColor="#15CF74"/>
          <stop offset=".8" stopColor="#06E775"/>
          <stop offset="1" stopColor="#00F076"/>
        </linearGradient>
      </defs>
    </svg>
  </a>
);

// Main Landing Page Component
const LandingPage = ({ theme, toggleTheme }) => {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      icon: '📊',
      title: 'Market Cap Tracking',
      description: 'Real-time tracking of market cap movements across all major platforms. Never miss a pump again.'
    },
    {
      icon: '🎓',
      title: 'Just Graduated Coins',
      description: 'Instant alerts when coins graduate from launch platforms. Be first to spot new opportunities.'
    },
    {
      icon: '🤖',
      title: 'AI-Powered Insights',
      description: 'Alphie, our AI companion, analyzes patterns and surfaces high-potential tokens before they trend.'
    },
    {
      icon: '🔮',
      title: 'Polymarket Predictions',
      description: 'Track prediction markets and sentiment. See what the crowd is betting on for crypto, politics, and more.'
    },
    {
      icon: '👁️',
      title: 'Real-time Watchlist',
      description: 'Build your personalized watchlist and get instant notifications on price movements and milestones.'
    }
  ];

  const stats = [
    { value: 1000, suffix: '+', label: 'Coins Tracked' },
    { value: 24, suffix: '/7', label: 'Monitoring' },
    { value: 100, suffix: 'ms', label: 'Update Speed' }
  ];

  return (
    <div className={`relative min-h-screen overflow-hidden ${theme === 'dark' ? 'dark' : ''}`}>
      {/* Background */}
      <div className="fixed inset-0 bg-gradient-to-br from-alpha-50 via-white to-silver-100 dark:from-navy-950 dark:via-navy-900 dark:to-alpha-950 transition-colors duration-500" />
      <ConstellationCanvas theme={theme} />

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/80 dark:bg-navy-900/80 backdrop-blur-xl shadow-lg'
          : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-2">
          {/* Brand - logo always visible, text hidden on very small screens */}
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <img src="/logo.png" alt="HeyAlphas" className="w-9 h-9 sm:w-10 sm:h-10" />
            <span className="hidden xs:inline text-xl sm:text-2xl font-black tracking-tight text-silver-800 dark:text-white">
              Hey<span className="text-alpha-500">Alphas</span>
            </span>
          </div>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-silver-600 dark:text-silver-300 hover:text-alpha-500 dark:hover:text-alpha-400 transition-colors font-medium">
              Features
            </a>
            <a href="#how-it-works" className="text-silver-600 dark:text-silver-300 hover:text-alpha-500 dark:hover:text-alpha-400 transition-colors font-medium">
              How It Works
            </a>
            <a href="#stats" className="text-silver-600 dark:text-silver-300 hover:text-alpha-500 dark:hover:text-alpha-400 transition-colors font-medium">
              Stats
            </a>
          </div>

          {/* Right side actions */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 sm:p-2.5 rounded-xl bg-silver-100 dark:bg-navy-800 text-silver-600 dark:text-silver-300 hover:bg-silver-200 dark:hover:bg-navy-700 transition-all duration-300 shrink-0"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? (
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" />
                </svg>
              ) : (
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              )}
            </button>

            {/* App store badges */}
            <div className="flex items-center gap-1.5 sm:gap-2">
              <AppStoreBadge height={28} className="sm:hidden" />
              <GooglePlayBadge height={28} className="sm:hidden" />
              <AppStoreBadge height={32} className="hidden sm:inline-block" />
              <GooglePlayBadge height={32} className="hidden sm:inline-block" />
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-6 pt-20">
        <div className="max-w-6xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-alpha-100/80 dark:bg-alpha-900/30 border border-alpha-200 dark:border-alpha-700/30 backdrop-blur-sm mb-8 animate-fade-in">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-alpha-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-alpha-500"></span>
            </span>
            <span className="text-sm font-medium text-alpha-700 dark:text-alpha-300">
              Now tracking 1,000+ tokens
            </span>
          </div>

          {/* Logo */}
          <div className="mb-8 animate-float">
            <img src="/logo.png" alt="HeyAlphas" className="w-32 h-32 mx-auto drop-shadow-2xl" />
          </div>

          {/* Headline */}
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tight mb-6 animate-slide-up">
            <span className="text-silver-800 dark:text-white">Discover the</span>
            <br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-alpha-400 via-alpha-500 to-alpha-700 dark:from-alpha-300 dark:via-alpha-400 dark:to-alpha-600">
              Next Alpha
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-xl md:text-2xl text-silver-600 dark:text-silver-300 max-w-3xl mx-auto mb-10 leading-relaxed animate-slide-up" style={{ animationDelay: '100ms' }}>
            Track graduated coins, analyze market insights, and discover high-potential tokens
            <span className="text-alpha-500 font-semibold"> before they moon</span>.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slide-up" style={{ animationDelay: '200ms' }}>
            <div className="flex items-center gap-3">
              <AppStoreBadge height={48} />
              <GooglePlayBadge height={48} />
            </div>

            <a
              href="#features"
              className="px-8 py-4 text-silver-700 dark:text-silver-200 font-bold text-lg rounded-2xl border-2 border-silver-300 dark:border-silver-600 hover:border-alpha-500 dark:hover:border-alpha-400 hover:text-alpha-600 dark:hover:text-alpha-400 transition-all duration-300"
            >
              Learn More
            </a>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
            <svg className="w-6 h-6 text-silver-400 dark:text-silver-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="relative py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-silver-800 dark:text-white mb-4">
              Powerful <span className="text-alpha-500">Features</span>
            </h2>
            <p className="text-xl text-silver-600 dark:text-silver-400 max-w-2xl mx-auto">
              Everything you need to stay ahead in the fast-moving world of crypto
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <FeatureCard
                key={feature.title}
                {...feature}
                delay={index * 100}
                theme={theme}
              />
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="relative py-32 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-black text-silver-800 dark:text-white mb-4">
              How It <span className="text-alpha-500">Works</span>
            </h2>
            <p className="text-xl text-silver-600 dark:text-silver-400">
              Three simple steps to finding your next alpha
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12 md:gap-8">
            <Step
              number="1"
              title="Track"
              description="Monitor real-time data from multiple platforms including Raydium and Pump.fun"
            />
            <Step
              number="2"
              title="Analyze"
              description="Let Alphie AI identify patterns and surface high-potential opportunities"
            />
            <Step
              number="3"
              title="Discover"
              description="Get alerted to promising tokens before they trend and make your move"
              isLast
            />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section id="stats" className="relative py-32 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div
                key={stat.label}
                className="text-center p-8 rounded-3xl bg-white/50 dark:bg-navy-800/30 backdrop-blur-xl border border-silver-200/50 dark:border-silver-700/20"
              >
                <div className="text-5xl md:text-6xl font-black text-alpha-500 mb-2">
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-silver-600 dark:text-silver-400 font-medium text-lg">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="relative overflow-hidden rounded-[3rem] bg-alpha-gradient p-12 md:p-16 text-center shadow-alpha-lg">
            {/* Background pattern */}
            <div className="absolute inset-0 constellation-bg opacity-30" />

            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
                Ready to Find Alpha?
              </h2>
              <p className="text-xl text-alpha-100 mb-8 max-w-xl mx-auto">
                Join thousands of traders who discover opportunities before the crowd.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-16 px-6 border-t border-silver-200 dark:border-silver-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img src="/logo.png" alt="HeyAlphas" className="w-10 h-10" />
                <span className="text-2xl font-black tracking-tight text-silver-800 dark:text-white">
                  Hey<span className="text-alpha-500">Alphas</span>
                </span>
              </div>
              <p className="text-silver-600 dark:text-silver-400 max-w-sm">
                The ultimate platform for discovering crypto alpha opportunities. Track, analyze, and discover before the crowd.
              </p>
            </div>

            <div>
              <h4 className="font-bold text-silver-800 dark:text-white mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><a href="/terms" className="text-silver-600 dark:text-silver-400 hover:text-alpha-500 transition-colors">Terms of Service</a></li>
                <li><a href="/privacy" className="text-silver-600 dark:text-silver-400 hover:text-alpha-500 transition-colors">Privacy Policy</a></li>
                <li><a href="/support" className="text-silver-600 dark:text-silver-400 hover:text-alpha-500 transition-colors">Support</a></li>
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="pt-8 border-t border-silver-200 dark:border-silver-800 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-silver-500 dark:text-silver-500 text-sm">
              © {new Date().getFullYear()} HeyAlphas. All rights reserved.
            </p>

            {/* Social links */}
            <div className="flex items-center gap-4">
              <a href="https://x.com/heyalphas_" target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-silver-100 dark:bg-navy-800 text-silver-600 dark:text-silver-400 hover:text-alpha-500 dark:hover:text-alpha-400 transition-colors" aria-label="Twitter">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
